"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { CcFaqPillTabs } from "@/components/ui/CcFaqPillTabs";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import { Mark } from "@/components/services/shared/demo-parts";

import { CcAutoFlow } from "./CcAutoFlow";
import type { AiAutomationDemoCopy } from "./ai-automation-demo-copy";
import { useAutoShowcaseShellHeight } from "./useAutoShowcaseShellHeight";

type CcAutoShowcaseProps = {
  copy: AiAutomationDemoCopy["showcase"];
};

/** Before/after work flows at one made-up company: intro, case pills, both flows visible. */
const AUTO_SHOWCASE_ENTER_MS = 560;

export function CcAutoShowcase({ copy }: CcAutoShowcaseProps) {
  const [activeId, setActiveId] = useState(copy.cases[0]?.id ?? "");
  const [entering, setEntering] = useState(false);
  const prefersReducedMotion = useCcPrefersReducedMotion();
  const introShellRef = useRef<HTMLDivElement>(null);
  const introInnerRef = useRef<HTMLDivElement>(null);
  const flowsShellRef = useRef<HTMLDivElement>(null);
  const flowsInnerRef = useRef<HTMLDivElement>(null);
  const enterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const active = copy.cases.find((item) => item.id === activeId) ?? copy.cases[0];

  const selectCase = useCallback(
    (id: string) => {
      if (id === activeId) return;
      setActiveId(id);
      if (prefersReducedMotion) return;
      setEntering(true);
      if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = setTimeout(() => setEntering(false), AUTO_SHOWCASE_ENTER_MS);
    },
    [activeId, prefersReducedMotion],
  );

  useAutoShowcaseShellHeight(
    introShellRef,
    introInnerRef,
    prefersReducedMotion,
    activeId,
    active?.title,
    active?.text,
    active?.points,
  );

  useAutoShowcaseShellHeight(
    flowsShellRef,
    flowsInnerRef,
    prefersReducedMotion,
    activeId,
    active?.before,
    active?.after,
  );

  useEffect(
    () => () => {
      if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current);
    },
    [],
  );

  if (!active) return null;

  const copyBlocks = (
    <div className="cc-web-showcase__copies">
      {copy.cases.map((item) => {
        const isActive = item.id === active.id;
        return (
          <div
            key={item.id}
            className="cc-web-showcase__copy"
            data-active={isActive}
            aria-hidden={!isActive}
            inert={!isActive}
          >
            <CcSanjayaEyebrow theme="on-subtle">{item.name}</CcSanjayaEyebrow>
            <h3 className="cc-web-showcase__title">{item.title}</h3>
            <p className="cc-web-showcase__text">{item.text}</p>
            <ul className="cc-web-showcase__points">
              {item.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );

  const casePills = (
    <div className="cc-auto-showcase__case-switch-shell">
      <CcFaqPillTabs
        className="cc-auto-showcase__case-switch"
        items={copy.cases.map((item) => ({
          id: item.id,
          label: (
            <>
              <Mark logo={item.logo} badge={item.badge} />
              {item.name}
            </>
          ),
        }))}
        activeId={active.id}
        onSelect={selectCase}
        ariaLabel={copy.tablistLabel}
      />
    </div>
  );

  return (
    <section
      className="cc-section cc-web-showcase-section cc-ai-showcase-section cc-auto-showcase-section"
      aria-labelledby="cc-auto-showcase-title"
      data-cc-nav="light"
      {...devSectionAttr("auto-showcase")}
    >
      <div className="cc-container">
        <h2 id="cc-auto-showcase-title" className="cc-web-showcase__sr">
          {copy.sectionTitle}
        </h2>
        <div className="cc-web-showcase cc-auto-showcase">
          <div className="cc-auto-showcase__intro-row">
            <div className="cc-auto-showcase__intro-copy-shell" ref={introShellRef}>
              <div
                ref={introInnerRef}
                className="cc-auto-showcase__intro-copy"
                data-entering={entering ? "true" : undefined}
              >
                {copyBlocks}
              </div>
            </div>
            {casePills}
          </div>

          <figure
            id="cc-auto-showcase-tables"
            className="cc-web-showcase__media cc-auto-showcase__demo"
          >
            <div className="cc-auto-showcase__flows-shell" ref={flowsShellRef}>
              <div
                className="cc-auto-showcase__flows"
                ref={flowsInnerRef}
                data-entering={entering ? "true" : undefined}
              >
                <div className="cc-auto-showcase__flow-panel">
                  <CcAutoFlow
                    key={`${active.id}-before`}
                    name={active.name}
                    logo={active.logo}
                    badge={active.badge}
                    stateLabel={copy.beforeLabel}
                    stateVariant="before"
                    flow={active.before}
                    who={copy.who}
                    totalLabel={copy.totalLabel}
                  />
                </div>
                <div className="cc-auto-showcase__flow-panel">
                  <CcAutoFlow
                    key={`${active.id}-after`}
                    name={active.name}
                    logo={active.logo}
                    badge={active.badge}
                    stateLabel={copy.afterLabel}
                    stateVariant="after"
                    flow={active.after}
                    who={copy.who}
                    totalLabel={copy.totalLabel}
                  />
                </div>
              </div>
            </div>
          </figure>
        </div>
      </div>
    </section>
  );
}
